import { useEffect, useRef, useState } from 'react';
import { apiGet, apiPost } from '../../lib/api-client';
import { formatMoney, penceToPoundsInput, poundsToPence } from '../../lib/money';
import { invoiceAmountWithinRemaining, invoiceCandidatesForOrder, percentageAmountPence, percentageForAmount } from '../../lib/invoice-create';
import { Button } from '../ui/Button';
import { Dialog } from '../ui/Dialog';
import { Field, Input } from '../ui/Input';
import { useNotice } from '../ui/Notice';

interface BillableLine {
  poLineId: string;
  chargeLineId: string;
  reference: string;
  description: string;
  basisPence: number;
  remainingPence: number;
  selected: boolean;
  percentage: string;
  pounds: string;
}

interface ChargeRow {
  id: string;
  name: string;
  kind?: string;
  engagementId?: string;
  poId?: string;
  qty?: number;
  unitPence?: number;
  remainingPence?: number;
}

export function CreateInvoiceDialog({
  purchaseOrderId,
  engagementId,
  currency,
  open,
  onClose,
  onCreated,
}: {
  purchaseOrderId: string;
  engagementId: string;
  currency: string;
  open: boolean;
  onClose: () => void;
  onCreated: (invoiceId: string) => void;
}) {
  const notice = useNotice();
  const [billable, setBillable] = useState<BillableLine[]>([]);
  const [emptyReason, setEmptyReason] = useState('Every line on this order is already invoiced.');
  const [poNumber, setPoNumber] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const invoiceNumberTouched = useRef(false);
  const selected = billable.filter((line) => line.selected);
  const invoiceChargeLineId = selected[0]?.chargeLineId || '';
  const selectedAmountsValid = selected.length > 0 && selected.every((line) => invoiceAmountWithinRemaining(poundsToPence(line.pounds), line.remainingPence));

  useEffect(() => {
    if (!open) return;
    invoiceNumberTouched.current = false;
    setInvoiceNumber('');
    void Promise.all([
      apiGet<Record<string, unknown>[]>('/api/purchase-orders'),
      apiGet<ChargeRow[]>('/api/charge-lines'),
    ]).then(([list, charges]) => {
      const row = (list || []).find((item) => item.id === purchaseOrderId);
      setPoNumber(String(row?.poNumber || ''));
      const lines = ((row?.lines as Record<string, unknown>[]) || []).flatMap((line) => {
        const remainingPence = Number(line.remainingPence);
        const lineId = String(line.id || '');
        if (!lineId || !Number.isInteger(remainingPence) || remainingPence <= 0) return [];
        return [{
          id: lineId,
          reference: String(line.reference || ''),
          description: String(line.description || ''),
          amountPence: Number(line.amountPence),
          remainingPence,
        }];
      });
      const candidates = invoiceCandidatesForOrder(lines, charges || [], purchaseOrderId);
      setBillable(candidates.map((candidate) => ({
        ...candidate,
        selected: false,
        percentage: percentageForAmount(candidate.remainingPence, candidate.basisPence),
        pounds: penceToPoundsInput(candidate.remainingPence),
      })));
      setEmptyReason(lines.length
        ? 'No eligible charge lines remain on this order. Check that charge lines are linked to this order and have a positive unbilled amount.'
        : 'Every line on this order is already invoiced.');
    }).catch(() => {
      setPoNumber('');
      setBillable([]);
      setEmptyReason('The available order and charge lines could not be loaded.');
    });
  }, [open, purchaseOrderId]);

  useEffect(() => {
    if (!open || invoiceNumberTouched.current || !invoiceChargeLineId || !engagementId) return;
    const handle = window.setTimeout(() => {
      void apiGet<{ id: string; customerId?: string }[]>('/api/engagements').then((rows) => {
        const customerId = (rows || []).find((row) => row.id === engagementId)?.customerId || '';
        if (!customerId || invoiceNumberTouched.current) return;
        return apiGet<{ number: string }>(`/api/invoices/next-number?customerId=${encodeURIComponent(customerId)}&chargeLineId=${encodeURIComponent(invoiceChargeLineId)}`);
      }).then((row) => {
        if (!invoiceNumberTouched.current && row?.number) setInvoiceNumber(row.number);
      }).catch(() => undefined);
    }, 200);
    return () => window.clearTimeout(handle);
  }, [open, invoiceChargeLineId, engagementId]);

  async function createInvoice() {
    const chosen = selected;
    if (!chosen.length) {
      notice.failure('Select at least one charge line.');
      return;
    }
    const lines = chosen.map((line) => ({
      ...line,
      amountPence: poundsToPence(line.pounds),
    }));
    if (lines.some((line) => !invoiceAmountWithinRemaining(line.amountPence, line.remainingPence))) {
      notice.failure('Each selected line needs an amount up to what is left on that line.');
      return;
    }
    const row = await notice.run('Creating the draft invoice', () => apiPost<{ id: string }>('/api/invoices', {
      engagementId,
      chargeLineId: invoiceChargeLineId,
      ...(invoiceNumber ? { number: invoiceNumber, internalNumber: invoiceNumber } : {}),
      issuedOn: new Date().toISOString().slice(0, 10),
      currency,
      lines: lines.map((line) => ({
        description: line.description,
        qty: 1,
        unitPence: line.amountPence,
        amountPence: line.amountPence,
        source: 'manual',
        poId: purchaseOrderId,
        poLineId: line.poLineId,
        chargeLineIds: [line.chargeLineId],
      })),
    }), 'Draft invoice created', { confirm: false });
    if (!row?.id) return;
    onCreated(row.id);
  }

  if (!open) return null;
  return (
    <Dialog title="Create invoice" large onClose={onClose} footer={
      <>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={() => void createInvoice()} disabled={!selectedAmountsValid || !invoiceChargeLineId}>Create draft</Button>
      </>
    }>
      <p className="text-sm text-[var(--bo-muted)]">Select the charge lines to invoice and confirm each amount. Every charge stays linked to its purchase-order line. Amounts start at the unbilled balance and cannot exceed it.</p>
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Purchase order"><Input value={poNumber} disabled /></Field>
        <Field label="Invoice number"><Input value={invoiceNumber} onChange={(event) => { invoiceNumberTouched.current = true; setInvoiceNumber(event.target.value); }} /></Field>
      </div>
      {billable.length ? (
        <div className="space-y-2">
          <label className="flex items-center gap-3 text-sm">
            <input
              type="checkbox"
              aria-label="Select all lines"
              checked={billable.every((line) => line.selected)}
              onChange={(event) => {
                const on = event.target.checked;
                setBillable((rows) => rows.map((item) => ({
                  ...item,
                  selected: on,
                  percentage: on ? percentageForAmount(item.remainingPence, item.basisPence) : item.percentage,
                  pounds: on ? penceToPoundsInput(item.remainingPence) : item.pounds,
                })));
              }}
            />
            Select all
          </label>
          {billable.map((line) => (
              <div key={`${line.poLineId}:${line.chargeLineId}`} className="grid grid-cols-[24px_minmax(0,1fr)_120px_160px] items-start gap-3">
                <input type="checkbox" aria-label={`Invoice ${line.description}`} checked={line.selected} onChange={(event) => setBillable((rows) => rows.map((item) => item.poLineId === line.poLineId && item.chargeLineId === line.chargeLineId ? {
                  ...item,
                  selected: event.target.checked,
                  percentage: event.target.checked ? percentageForAmount(item.remainingPence, item.basisPence) : item.percentage,
                  pounds: event.target.checked ? penceToPoundsInput(item.remainingPence) : item.pounds,
                } : item))} />
                <div>
                  <div className="text-sm">{[line.reference, line.description].filter(Boolean).join(' - ')}</div>
                  <div className="text-xs text-[var(--bo-muted)]">Agreed net {formatMoney(line.basisPence, currency)}</div>
                  <div className="text-xs text-[var(--bo-muted)]">Left {formatMoney(line.remainingPence, currency)}</div>
                  {line.selected ? <div className="mt-1 text-xs text-[var(--bo-muted)]">Linked to {line.description}</div> : null}
                </div>
                <label className="text-xs text-[var(--bo-muted)]">
                  Percentage
                  <Input aria-label={`Percentage for ${line.description}`} type="number" min="0" step="0.01" disabled={!line.selected} value={line.selected ? line.percentage : ''} onChange={(event) => setBillable((rows) => rows.map((item) => item.poLineId === line.poLineId && item.chargeLineId === line.chargeLineId ? {
                    ...item,
                    percentage: event.target.value,
                    pounds: penceToPoundsInput(percentageAmountPence(item.basisPence, event.target.value)),
                  } : item))} />
                </label>
                <label className="text-xs text-[var(--bo-muted)]">
                  Net amount
                  <Input aria-label={`Amount for ${line.description}`} type="number" step="0.01" disabled={!line.selected} value={line.selected ? line.pounds : ''} onChange={(event) => setBillable((rows) => rows.map((item) => item.poLineId === line.poLineId && item.chargeLineId === line.chargeLineId ? {
                    ...item,
                    percentage: percentageForAmount(poundsToPence(event.target.value), item.basisPence),
                    pounds: event.target.value,
                  } : item))} />
                </label>
              </div>
          ))}
          {selected.length ? (
            <div className="mt-4 rounded-lg border border-[var(--bo-border)] p-3">
              <div className="mb-2 text-sm font-semibold">Invoice preview — {selected.length} selected line{selected.length === 1 ? '' : 's'}</div>
              <div className="space-y-1 text-sm">
                {selected.map((line) => <div key={`${line.poLineId}:${line.chargeLineId}`} className="flex justify-between gap-4"><span>{[line.reference, line.description].filter(Boolean).join(' - ')}<span className="block text-xs text-[var(--bo-muted)]">Basis {formatMoney(line.basisPence, currency)} × {line.percentage || '0'}%</span></span><span className="tabular-nums">{formatMoney(poundsToPence(line.pounds), currency)} net</span></div>)}
              </div>
              {!selectedAmountsValid ? <p className="mt-2 text-sm text-red-600">The calculated net amount must be positive and cannot exceed the unbilled balance.</p> : null}
            </div>
          ) : null}
        </div>
      ) : <p className="text-sm">{emptyReason}</p>}
    </Dialog>
  );
}
